import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Sparkles, 
  Check, 
  Calendar, 
  Clock, 
  Car, 
  User, 
  Mail, 
  Phone, 
  MapPin,
  Download,
  Copy,
  Home,
  Gift,
  Droplets,
  Banknote
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { parseBookingDate, formatTimeDisplay } from "@/utils/dateUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ETRANSFER_EMAIL = "jahh88@icloud.com";

export default function ConfirmationPage() {
  const { bookingId } = useParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBookingData = async () => {
      try {
        const res = await axios.get(`${API}/bookings/${bookingId}`);
        setBooking(res.data);
      } catch (err) {
        toast.error("Failed to load booking details");
      } finally {
        setLoading(false);
      }
    };
    fetchBookingData();
  }, [bookingId]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  // Use imported formatTimeDisplay from utils
  const formatTime = formatTimeDisplay;

  const printInvoice = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <h2 className="text-xl font-bold mb-4">Booking Not Found</h2>
          <p className="text-muted-foreground mb-6">
            We couldn&apos;t find this booking. Please check the URL or contact us.
          </p>
          <Link to="/">
            <Button>Return Home</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-slate-900 border-b border-white/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-sky-400" />
              <span className="logo-text text-lg text-white">RISE & SHINE</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="confirmation-page">
        {/* Success Banner */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground uppercase tracking-tight mb-2">
            Booking Confirmed!
          </h1>
          <p className="text-muted-foreground">
            Thank you for choosing Rise & Shine Auto Detailing
          </p>
        </div>

        {/* Invoice */}
        <Card className="invoice-container mb-8 print:shadow-none" id="invoice" data-testid="booking-invoice">
          <CardContent className="p-8">
            {/* Invoice Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-8 pb-6 border-b border-border">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-8 h-8 text-sky-500" />
                  <span className="logo-text text-2xl text-foreground">RISE & SHINE</span>
                </div>
                <p className="text-muted-foreground">Auto Detailing</p>
                <p className="text-muted-foreground">905 869-1069</p>
              </div>
              <div className="text-left sm:text-right">
                <h2 className="text-2xl font-bold text-foreground mb-2">INVOICE</h2>
                <p className="text-muted-foreground text-sm">Invoice #: {booking.invoice_number}</p>
                <p className="text-muted-foreground text-sm">
                  Date: {format(new Date(booking.created_at), 'MMM d, yyyy')}
                </p>
              </div>
            </div>

            {/* Service Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-4">Service Details</h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-sky-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-foreground">{booking.service_name}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Calendar className="w-5 h-5 text-sky-500 mt-0.5" />
                    <div>
                      <p className="text-foreground">
                        {format(parseBookingDate(booking.date), 'EEEE, MMMM d, yyyy')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-sky-500 mt-0.5" />
                    <div>
                      <p className="text-foreground">{formatTime(booking.time_slot)}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-4">Vehicle</h3>
                <div className="flex items-start gap-3">
                  <Car className="w-5 h-5 text-sky-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">
                      {booking.vehicle.make} {booking.vehicle.model}
                    </p>
                    <p className="text-muted-foreground">License: {booking.vehicle.license_plate}</p>
                    {booking.vehicle.color && (
                      <p className="text-muted-foreground">Color: {booking.vehicle.color}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Client Info */}
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-4">Bill To</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-sky-500 mt-0.5" />
                  <p className="text-foreground">{booking.client.name}</p>
                </div>
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-sky-500 mt-0.5" />
                  <p className="text-foreground">{booking.client.email}</p>
                </div>
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-sky-500 mt-0.5" />
                  <p className="text-foreground">{booking.client.phone}</p>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-sky-500 mt-0.5" />
                  <p className="text-foreground">{booking.client.address}</p>
                </div>
              </div>
            </div>

            {/* Location Info */}
            {booking.location && (
              <div className="mb-8 p-4 bg-sky-500/5 rounded-lg">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-2">Service Location Details</h3>
                <div className="space-y-2 text-sm">
                  {booking.location.drop_off_location && (
                    <p className="text-sky-600 font-medium">
                      <MapPin className="w-4 h-4 inline mr-1" />
                      Drop-off location service
                    </p>
                  )}
                  {booking.location.bring_hose && (
                    <p className="text-foreground">
                      <Droplets className="w-4 h-4 inline mr-1 text-sky-500" />
                      We will bring a hose
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Special Requests */}
            {booking.special_requests && (
              <div className="mb-8 p-4 bg-muted rounded-lg">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase mb-2">Special Requests</h3>
                <p className="text-foreground">{booking.special_requests}</p>
              </div>
            )}

            {/* Discount Code Applied */}
            {booking.discount_code && booking.discount > 0 && (
              <div className="mb-4 p-4 bg-green-500/10 rounded-lg">
                <h3 className="text-sm font-semibold text-green-700 uppercase mb-2 flex items-center gap-2">
                  <Gift className="w-4 h-4" />
                  Promo Code Applied
                </h3>
                <p className="text-green-600">
                  Code: <strong>{booking.discount_code}</strong> — {booking.discount_description}
                </p>
                <p className="text-green-600">
                  You saved ${booking.discount.toFixed(2)}!
                </p>
              </div>
            )}

            {/* Referral Discount */}
            {booking.referral_code && booking.discount > 0 && !booking.discount_code && (
              <div className="mb-4 p-4 bg-green-500/10 rounded-lg">
                <h3 className="text-sm font-semibold text-green-700 uppercase mb-2 flex items-center gap-2">
                  <Gift className="w-4 h-4" />
                  Referral Discount Applied
                </h3>
                <p className="text-green-600">
                  Referred by: {booking.referral_code} — You saved ${booking.discount.toFixed(2)}!
                </p>
              </div>
            )}

            {/* Total */}
            <div className="border-t border-border pt-6">
              {booking.discount > 0 && (
                <div className="flex justify-between items-center mb-2">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="text-muted-foreground">${booking.service_price.toFixed(2)}</span>
                </div>
              )}
              {booking.discount > 0 && (
                <div className="flex justify-between items-center mb-2 text-green-600">
                  <span>Referral Discount</span>
                  <span>-${booking.discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="text-lg text-muted-foreground">Total Due</span>
                <span className="text-3xl font-bold text-sky-500" data-testid="invoice-total">
                  ${(booking.final_price || booking.service_price).toFixed(2)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Instructions */}
        <Card className="mb-8 bg-sky-500/5 border-sky-500/20" data-testid="payment-instructions">
          <CardContent className="p-6">
            <h3 className="text-xl font-bold text-foreground mb-4">Payment Options</h3>
            <p className="text-muted-foreground mb-4">
              Please complete your payment before or at your appointment:
            </p>
            
            {/* E-Transfer Option */}
            <div className="mb-4">
              <h4 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                <Mail className="w-5 h-5 text-sky-500" />
                Option 1: Interac e-Transfer
              </h4>
              <div className="flex items-center gap-4 p-4 bg-background rounded-lg border border-border">
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Send e-Transfer to:</p>
                  <p className="text-lg font-medium text-foreground">{ETRANSFER_EMAIL}</p>
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => copyToClipboard(ETRANSFER_EMAIL)}
                  data-testid="copy-email-btn"
                >
                  <Copy className="w-4 h-4 mr-1" />
                  Copy
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                Include invoice number <strong>{booking.invoice_number}</strong> in the message.
              </p>
            </div>

            {/* Cash Option */}
            <div className="p-4 bg-green-500/5 rounded-lg border border-green-500/20">
              <h4 className="font-semibold text-foreground mb-1 flex items-center gap-2">
                <Banknote className="w-5 h-5 text-green-600" />
                Option 2: Cash
              </h4>
              <p className="text-sm text-muted-foreground">
                Pay cash at the time of service. Exact change appreciated.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center print:hidden">
          <Button 
            variant="outline" 
            onClick={printInvoice}
            className="px-6"
            data-testid="print-invoice-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Print Invoice
          </Button>
          <Link to="/">
            <Button className="bg-sky-500 hover:bg-sky-600 px-6 w-full sm:w-auto">
              <Home className="w-4 h-4 mr-2" />
              Return Home
            </Button>
          </Link>
        </div>

        {/* Contact Info */}
        <div className="text-center mt-12 text-muted-foreground print:hidden">
          <p>Questions? Contact us at</p>
          <p className="font-medium text-foreground">905 869-1069 or {ETRANSFER_EMAIL}</p>
        </div>
      </main>
    </div>
  );
}
